import type { AppointmentState, CustomerInfo, ShopData } from "../types";

import { captureBooking } from "./api";
import { validateNumber } from "./validation";

type BailoutCustomer = Required<
  Pick<CustomerInfo, "phone" | "fname" | "lname" | "email">
>;

type AppointmentWithBailout = AppointmentState & {
  previousStage?: number;
  bailOutStage?: {
    bailOutStep?: number;
    bailOutComments?: string;
  };
};

interface BailoutStage {
  bailOutStep: number;
  bailOutComments: string;
}

export interface BailoutPayload {
  customer: BailoutCustomer;
  bailOutStage: BailoutStage;
  shopId?: string;
  domain?: string;
  shopData?: ShopData;
  date?: string;
  time?: string;
  service?: string;
  bookingId?: string;
  bailed: boolean;
  sourceQueryString?: string;
}

interface BuildBailoutPayloadArgs {
  appointment?: AppointmentWithBailout;
  customer?: Partial<BailoutCustomer>;
  comments?: string;
  bailOutStep?: number;
  shopId?: string;
  shopData?: ShopData;
}

interface CaptureBookingResponse {
  insertedId?: unknown;
}

interface EnsureBookingIdArgs<TAction = unknown> {
  appointment?: AppointmentWithBailout;
  payload: object;
  dispatch?: (action: TAction) => unknown;
  updateAppointmentAction?: (args: {
    updates: Partial<AppointmentState>;
  }) => TAction;
}

interface CaptureBailoutArgs<TAction = unknown> extends EnsureBookingIdArgs<TAction> {
  payload: BailoutPayload;
}

export const buildBailoutPayload = ({
  appointment,
  customer = {},
  comments,
  bailOutStep,
  shopId,
  shopData,
}: BuildBailoutPayloadArgs): BailoutPayload => {
  const resolvedShopId = shopId || appointment?.shopData?.shopId;
  const resolvedShopData = shopData || appointment?.shopData;

  return {
    customer: {
      phone: customer.phone || appointment?.customer?.phone || "",
      fname: customer.fname || appointment?.customer?.fname || "",
      lname: customer.lname || appointment?.customer?.lname || "",
      email: customer.email || appointment?.customer?.email || "",
    },
    bailOutStage: {
      bailOutStep:
        bailOutStep ??
        appointment?.previousStage ??
        appointment?.bailOutStage?.bailOutStep ??
        appointment?.stage ??
        0,
      bailOutComments:
        comments ||
        appointment?.bailOutStage?.bailOutComments ||
        "Exited from bailout page without submitting form",
    },
    shopId: resolvedShopId,
    domain: appointment?.domain,
    shopData: resolvedShopData,
    date: appointment?.date,
    time: appointment?.time,
    service: appointment?.service,
    bookingId: appointment?.bookingId,
    bailed: true,
    sourceQueryString: appointment?.sourceQueryString,
  };
};

const inflightEnsureBookingId = new Map<string, Promise<string | undefined>>();

export const ensureBookingId = async <TAction>({
  appointment,
  payload,
  dispatch,
  updateAppointmentAction,
}: EnsureBookingIdArgs<TAction>): Promise<string | undefined> => {
  if (appointment?.bookingId) return appointment.bookingId;

  // Concurrent callers for the same session must share one network round-trip
  // so /capture is not invoked twice without a newAppointmentId, which would
  // produce duplicate "pending" appointment docs.
  const key = [
    appointment?.domain ?? "",
    appointment?.shopData?.shopId ?? "",
    appointment?.customer?.phone ?? "",
  ].join("|");

  const existing = inflightEnsureBookingId.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<string | undefined> => {
    const response = (await captureBooking({
      ...payload,
      bailed: false,
    })) as CaptureBookingResponse;
    const insertedId = response?.insertedId;
    const bookingId = insertedId ? String(insertedId) : undefined;

    if (bookingId && dispatch && updateAppointmentAction) {
      dispatch(
        updateAppointmentAction({
          updates: { bookingId, bailed: false },
        })
      );
    }

    return bookingId;
  })().finally(() => {
    inflightEnsureBookingId.delete(key);
  });

  inflightEnsureBookingId.set(key, promise);
  return promise;
};

// A bailout only becomes a lead (and a customer record) when the shop has a
// number to call back. Name, email or comments alone are not enough.
export const hasCallbackNumber = (phone?: string | null): boolean =>
  Boolean(phone && validateNumber(phone));

export const captureBailout = async <TAction>({
  appointment,
  payload,
  dispatch,
  updateAppointmentAction,
}: CaptureBailoutArgs<TAction>): Promise<unknown> => {
  if (!hasCallbackNumber(payload.customer.phone)) return undefined;

  const bookingId = await ensureBookingId({
    appointment,
    payload,
    dispatch,
    updateAppointmentAction,
  });

  return captureBooking({
    ...payload,
    bookingId,
    bailed: true,
  });
};
